import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { MarketGroup } from '../../utils/marketGroupUtils';
import LabelSegments from './LabelSegments';
import './MarketGroupLabelCell.css';

type MarketGroupLabelCellProps = {
  group: MarketGroup;
};

type TooltipPosition = {
  top: number;
  left: number;
};

const MarketGroupLabelCell: React.FC<MarketGroupLabelCellProps> = ({ group }) => {
  const [showRolesTip, setShowRolesTip] = useState(false);
  const [tooltipPos, setTooltipPos] = useState<TooltipPosition>({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const roleNames = group.clusterRoleNames ?? [];
  const showClusterHint = group.mode === 'cluster' && roleNames.length > 0;

  const updateTooltipPosition = useCallback(() => {
    const button = buttonRef.current;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const gap = 8;
    const estimatedWidth = 320;
    const spaceRight = window.innerWidth - rect.right - gap;
    const spaceLeft = rect.left - gap;

    let left = rect.right + gap;
    if (spaceRight < estimatedWidth && spaceLeft > spaceRight) {
      left = Math.max(gap, rect.left - gap - estimatedWidth);
    }

    const top = Math.min(
      Math.max(rect.top + rect.height / 2, 16),
      window.innerHeight - 16,
    );

    setTooltipPos({ top, left });
  }, []);

  const openTooltip = () => {
    updateTooltipPosition();
    setShowRolesTip(true);
  };

  const closeTooltip = () => {
    setShowRolesTip(false);
  };

  useEffect(() => {
    if (!showRolesTip) return undefined;

    const handleReposition = () => updateTooltipPosition();
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);

    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [showRolesTip, updateTooltipPosition]);

  return (
    <span className="market-group-label-cell">
      <span className="market-group-label-cell__name" title={group.label}>
        <LabelSegments
          label={group.label}
          segmentClassName="market-group-label-cell__segment"
          separatorClassName="market-group-label-cell__sep"
        />
      </span>
      {showClusterHint && (
        <span className="market-group-label-cell__hint">
          <button
            ref={buttonRef}
            type="button"
            className="market-group-label-cell__btn"
            aria-label={`Роли в кластере ${group.label}`}
            onMouseEnter={openTooltip}
            onMouseLeave={closeTooltip}
            onFocus={openTooltip}
            onBlur={closeTooltip}
          >
            ?
          </button>
          {showRolesTip && createPortal(
            <span
              role="tooltip"
              className="market-group-label-cell__tooltip"
              style={{
                top: tooltipPos.top,
                left: tooltipPos.left,
                transform: 'translateY(-50%)',
              }}
            >
              {roleNames.map((name) => (
                <span key={name} className="market-group-label-cell__tooltip-item">
                  <LabelSegments
                    label={name}
                    segmentClassName="market-group-label-cell__segment"
                    separatorClassName="market-group-label-cell__sep"
                  />
                </span>
              ))}
            </span>,
            document.body,
          )}
        </span>
      )}
    </span>
  );
};

export default MarketGroupLabelCell;
